// src/components/HomeRenderer.jsx
import React, { useMemo } from "react";
import ProductRow from "./ProductRow";
import ProductCard from "./ProductCard";

/** ✅ Stock : ne JAMAIS afficher hors stock */
const isInStockProduct = (p) => {
  const variants = Array.isArray(p?.variants) ? p.variants : [];
  if (variants.length > 0) return variants.some((v) => Number(v?.stock || 0) > 0);
  return Number(p?.stock || 0) > 0;
};

/** Dédup simple */
const dedupeByIdOrSlug = (arr = []) => {
  const out = [];
  const seen = new Set();
  for (const p of arr) {
    const k = p?.id || p?.slug;
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(p);
  }
  return out;
};

/**
 * HomeRenderer
 * Rend dynamiquement des sections de la page d'accueil à partir de données (ex: Firestore).
 *
 * Chaque section dans `sections` peut avoir un `type` parmi :
 *  - "productsRow":   { type, title, items: [product], seeAllHref? }
 *  - "featured":      { type, title?, items: [product], seeAllHref? }
 *  - "banner":        { type, html }
 *  - "html":          { type, html }
 *  - "custom":        { type, component?: ReactNode }
 *
 * ✅ Règle globale : on ne montre que les produits en stock.
 */
export default function HomeRenderer({ sections = [], onAdd = () => {} }) {
  const safeSections = useMemo(() => {
    if (!Array.isArray(sections)) return [];
    return sections
      .filter((s) => s && typeof s === "object")
      .map((s) => {
        const type = String(s.type || "").toLowerCase();

        // Si la section contient des produits, on filtre stock + dédup
        if (type === "productsrow" || type === "featured") {
          const rawItems = Array.isArray(s.items) ? s.items : [];
          const inStockItems = dedupeByIdOrSlug(rawItems.filter(isInStockProduct));
          return { ...s, items: inStockItems };
        }

        return s;
      })
      // Optionnel : si une section produit devient vide après filtrage, on la masque
      .filter((s) => {
        const type = String(s.type || "").toLowerCase();
        if (type === "productsrow" || type === "featured") {
          return Array.isArray(s.items) && s.items.length > 0;
        }
        return true;
      });
  }, [sections]);

  if (safeSections.length === 0) return null;

  return (
    <>
      {safeSections.map((s, idx) => {
        const key = s.id || s.key || `sec-${idx}`;
        const type = String(s.type || "").toLowerCase();

        switch (type) {
          case "productsrow": {
            const title = s.title || "Sélection";
            const items = Array.isArray(s.items) ? s.items : [];
            return (
              <ProductRow
                key={key}
                title={title}
                items={items}
                onAdd={onAdd}
                seeAllHref={s.seeAllHref}
              />
            );
          }

          case "featured": {
            const title = s.title || "À la Une";
            const items = Array.isArray(s.items) ? s.items : [];

            return (
              <section key={key} className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h5 className="m-0">{title}</h5>
                  {s.seeAllHref && (
                    <a href={s.seeAllHref} className="btn btn-dark">
                      Voir Catalogue →
                    </a>
                  )}
                </div>

                <div className="row row-cols-2 row-cols-sm-3 row-cols-lg-4 row-cols-xl-5 g-3">
                  {items.map((p, i) => (
                    <div className="col" key={p?.id || p?.slug || `${key}-${i}`}>
                      <ProductCard p={p} onAdd={onAdd} />
                    </div>
                  ))}
                </div>
              </section>
            );
          }

          case "banner":
          case "html": {
            if (!s.html) return null;
            return (
              <section key={key} className="mb-4">
                <div dangerouslySetInnerHTML={{ __html: s.html }} />
              </section>
            );
          }

          case "custom": {
            if (s.component) return <div key={key}>{s.component}</div>;
            return null;
          }

          default:
            return null;
        }
      })}
    </>
  );
}
